import React, { useEffect, useState } from "react";
import Button from "./Button";
import InputAmount from "./InputAmount";

import translator from "./../utils/translation";
import { ReactComponent as IconTextBold } from '../assets/icons/text-bold.svg'
import { ReactComponent as IconTextItalic } from '../assets/icons/text-italic.svg'
import { ReactComponent as IconTextUnderline } from '../assets/icons/text-underline.svg'

const SelectionTextSettings = ({ canvas, activeSelection }) => {
    const [fontFamily, setFontFamily] = useState();
    const [fontSize, setFontSize] = useState(null);
    const [lineHeight, setLineHeight] = useState(20);
    const [charSpacing, setCharSpacing] = useState(0);
    const [textAlign, setTextAlign] = useState("left");
    const [textColor, setTextColor] = useState(false);
    const [fontWeight, setFontWeight] = useState("");

    const setActiveStyle = (styleName, value) => {
        const activeObject = canvas?.getActiveObject();

        if (!activeObject) return;
        if (activeObject.isEditing && activeObject.type === "CustomTextbox") {
            const selection = activeObject.getSelectionStyles();

            setFontWeight(value);
            const style = {};
            style[styleName] = value;
            textConfiguration[styleName] = value;
            activeObject.setSelectionStyles(
                style,
                activeObject.selectionStart,
                activeObject.selectionEnd
            );
            activeObject.setCoords();
        } else {
            activeObject.set(styleName, value);
        }
        canvas.requestRenderAll();
        canvas.calcOffset();
    };

    const toggleText = (styleName, value) => {
        const activeObject = canvas?.getActiveObject();
        if (activeObject?.type === "CustomTextbox") {
            textConfiguration[styleName] = value;
            // Update local state for UI consistency
            setFontWeight(value);

            // Apply changes based on editing state
            if (activeObject.isEditing && activeObject.setSelectionStyles) {
                // Apply color to the selected text or current cursor position
                setActiveStyle(styleName, value);
            } else if (
                !activeObject.isEditing &&
                activeObject.setSelectionStyles
            ) {
                // Iterate over styles to update text color while preserving other styles
                const textLength = activeObject.text.length;
                for (let i = 0; i < textLength; i++) {
                    // Ensure there's a styles object for manipulating individual character styles
                    if (!activeObject.styles) activeObject.styles = {};
                    if (!activeObject.styles[0]) activeObject.styles[0] = {};

                    // Check if the character at position i has a style object, create if not
                    if (!activeObject.styles[0][i]) {
                        activeObject.styles[0][i] = {};
                    }
                    // Update the fill color while keeping other styles intact
                    activeObject.styles[0][i][styleName] = value;
                }

                // If there's no specific style set, apply the fill color directly to the object
                if (Object.keys(activeObject.styles[0]).length === 0) {
                    activeObject.set(styleName, value);
                }
            }
            // Request a re-render of the canvas
            canvas.requestRenderAll();
            canvas.calcOffset();
        }
    };

    const toggleBold = () => {
        if (!activeSelection) return;
        console.log("textConfiguration:", textConfiguration);
        toggleText(
            "fontWeight",
            textConfiguration.fontWeight === "bold" ? "normal" : "bold"
        );
        canvas.fire("object:modified");
    };

    const toggleItalic = () => {
        if (!activeSelection) return;
        toggleText(
            "fontStyle",
            textConfiguration.fontStyle === "italic" ? "" : "italic"
        );
        canvas.fire("object:modified");
    };

    const toggleUnderline = () => {
        if (!activeSelection) return;
        toggleText("underline", !textConfiguration.underline);
        canvas.fire("object:modified");
    };

    const handleSizeChange = (action, amount) => {
        if (!activeSelection) return;
        let size = 0;

        if (action === "decrease") {
            size = fontSize === 0 ? 0 : fontSize - 1;
        }

        if (action === "increase") {
            size = fontSize + 1;
        }

        if (action === "change") {
            size = parseInt(amount);
            if (!Number.isInteger(size)) return;
        }
        textConfiguration.fontSize = size;
        setFontSize(size);
        setActiveStyle("fontSize", size);
        canvas.fire("object:modified");
    };

    const setPredefinedValue = ({ target }) => {
        if (target?.get("type") === "CustomTextbox") {
            setFontFamily(target.fontFamily);
            setFontSize(target.fontSize);
            setTextColor(textConfiguration.fill);
        }
    };

    useEffect(() => {
        if (
            activeSelection ||
            canvas?.getActiveObject()?.get("type") === "CustomTextbox"
        ) {
            canvas?.on("mouse:down", setPredefinedValue);
        }
        return () => {
            canvas?.off("mouse:down", setPredefinedValue);
        };
    }, [activeSelection, fontSize]);

    return (
        <>
            <div className="setting font-settings flex flex small-buttons">
                <div className="setting">
                    <div className="function">
                        <InputAmount
                            className="text-input"
                            unit=""
                            value={fontSize || textConfiguration.fontSize}
                            handleDecrease={() => handleSizeChange("decrease")}
                            handleChange={(e) =>
                                handleSizeChange("change", e.target.value)
                            }
                            handleIncrease={() => handleSizeChange("increase")}
                        />
                    </div>
                </div>
                <Button
                    handleClick={() => toggleItalic()}
                    name="italic"
                    title={translator("Italic")}
                    className={
                        textConfiguration.fontStyle === "italic" ? "active" : ""
                    }
                >
                    <IconTextItalic />
                </Button>
                <Button
                    handleClick={() => toggleBold()}
                    name="bold"
                    title={translator("Bold")}
                    className={
                        textConfiguration.fontWeight === "bold" ? "active" : ""
                    }
                >
                    <IconTextBold />
                </Button>
                <Button
                    handleClick={() => toggleUnderline()}
                    name="underline"
                    title={translator("Underline")}
                    className={textConfiguration.underline ? "active" : ""}
                >
                    <IconTextUnderline />
                </Button>
            </div>
        </>
    );
};

export default SelectionTextSettings;
